import React from "react";
import Table, {TableActionFunction} from "../../../common/components/shared/table/table";
import Loader from "../../../common/components/shared/loader/loader";
import {PageableModel, SortDirection} from "../../../common/models/table";
import "./dictionary.scss";
import {Popup} from "../../../common/components/shared/popup/popup";
import {ReactComponent as RemoveIcon} from "../../../statics/icons/icon_remove.svg";
import {Api} from "../../../helpers/api";
import {AxiosResponse} from "axios";
import i18n from "../../../i18n/i18n";
import {NavLink} from "react-router-dom";
import {HttpErrorModel} from "../../../common/models/httpError";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import {ValidatedValue} from "../../../helpers/validatedValue";

interface DictionaryState<T> {
    dictionaryData: T[] | undefined | null;
    displayRemovePopup: boolean;
    errorMessage: string;
    removeItemResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    dictionaryDataResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    selectedElement: T | undefined;
    currentPage: number;
    isWaiting: boolean;
    orderBy: string | undefined;
    sortDirection: SortDirection | undefined;
    numberOfElements: number;
    selectedValue: ValidatedValue<string>;
    selectedTwoValue: ValidatedValue<string>;
}

export default abstract class Dictionary<T> extends React.Component <any, DictionaryState<T>> {
    canRemove: boolean;
    linkToModify: string;

    constructor(props: any) {
        super(props);

        this.state = {
            dictionaryData: undefined,
            displayRemovePopup: false,
            errorMessage: "",
            dictionaryDataResponse: undefined,
            selectedElement: undefined,
            removeItemResponse: undefined,
            currentPage: 0,
            isWaiting: false,
            orderBy: undefined,
            sortDirection: undefined,
            numberOfElements: 0,
            selectedValue: new ValidatedValue(""),
            selectedTwoValue: new ValidatedValue("")
        };

        this.canRemove = false;
        this.linkToModify = "";
    }

    private get dictionaryData() {
        return this.state.dictionaryData;
    }

    protected getData(getAllData: AxiosResponse<PageableModel<T>>) {
        this.setState({
            dictionaryData: getAllData?.data?.data || null,
            dictionaryDataResponse: getAllData,
            currentPage: getAllData?.data?.currentPage,
            numberOfElements: getAllData?.data?.numberOfElements || 0
        });
    }

    protected reloadList(response: AxiosResponse<PageableModel<T>>) {
        this.setState({dictionaryData: undefined});
        this.getData(response);
    }

    protected renderHeader(headerElement: JSX.Element) {
        return (
            <div className="dictionary-header">
                {headerElement}
            </div>
        );
    }

    protected renderWrapperContent(
        headers: string[],
        properties: string[],
        testId: string,
        actions?: TableActionFunction,
        renderCell?: (row: any, property: string) => JSX.Element,
        headerElement?: JSX.Element,
        sortableElements?: string[],
        sortingAction?: (orderBy: string, sortDirection: SortDirection) => void
    ) {
        if (!Api.isSuccess(this.state.dictionaryDataResponse)) {
            return (
                <HttpError
                    response={this.state.dictionaryDataResponse}
                    cause={i18n.t("dictionaries.retrievalErrorSuffix")}
                />
            );
        } else {
            if (this.dictionaryData === undefined) {
                return <Loader/>;
            } else {
                return (
                    <>
                        {headerElement ? this.renderHeader(headerElement) : null}
                        <div className="dictionary-content">
                            {this.dictionaryData && this.dictionaryData!.length && this.dictionaryData!.length > 0 ? (
                                <Table headers={headers} rows={this.state.dictionaryData!} properties={properties}
                                    testId={`${testId}-table`}
                                    actions={actions ? actions.bind(this) : actions}
                                    renderCell={renderCell}
                                    pageable={true}
                                    currentPage={this.state.currentPage}
                                    numberOfElements={this.state.numberOfElements}
                                    isOldPagination
                                    sortableElements={sortableElements}
                                    sortingAction={async (orderBy: string, sortDirection: SortDirection) => {
                                        if (sortingAction && orderBy && sortDirection) {
                                            sortingAction(orderBy, sortDirection);
                                        }
                                    }}
                                    orderBy={this.state.orderBy}
                                    sortDirection={this.state.sortDirection}
                                    isTableLoader={this.state.isWaiting}
                                />
                            ) : (
                                <Table
                                    headers={headers}
                                    rows={[]}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    currentPage={this.state.currentPage}
                                    numberOfElements={0}
                                    isOldPagination
                                />
                            )}
                        </div>
                    </>
                );
            }
        }
    }

    protected renderActions(element: T) {
        // @ts-ignore
        const elementId = element.id;

        return (
            <>
                {elementId && (
                    <>
                        {this.linkToModify && (
                            <NavLink to={`${this.linkToModify}/${elementId}`} className="modify"
                                data-testid={`modify-${elementId}`}>
                                {i18n.t("modify")}
                            </NavLink>
                        )}
                        {this.canRemove && (
                            <span className="remove" data-testid={`remove-${elementId}`} onClick={() => {
                                this.setState({selectedElement: element});
                                this.onRemoveAction();
                            }}>
                                {i18n.t("remove")}
                            </span>
                        )}
                    </>
                )}
            </>
        );
    }

    protected renderRemovePopup(confirmRemove: any) {
        const content = <div>
            <p>{i18n.t("dictionaries.removeAction.line1")}</p>
            <p>{i18n.t("dictionaries.removeAction.line2")}</p>
        </div>;

        return (
            <Popup open={this.state.displayRemovePopup}
                className="remove-popup popup-with-secondary-button-color"
                header={i18n.t("dictionaries.removeAction.header")}
                secondHeader={i18n.t("dictionaries.removeAction.secondHeader")}
                icon={<RemoveIcon/>}
                content={content}
                actionText={i18n.t("dictionaries.removeAction.actionText")}
                action={confirmRemove}
                cancel={() => this.setState({displayRemovePopup: false})}
                testId="remove-popup"
                httpError={
                    <>
                        {!Api.isSuccess(this.state.removeItemResponse) && (
                            <HttpError
                                response={this.state.removeItemResponse}
                                cause={this.state.errorMessage}
                            />
                        )}
                    </>
                }
                isWaiting={this.state.isWaiting}
            />
        );
    }

    protected onRemoveAction() {
        this.setState({displayRemovePopup: true, errorMessage: ""});
    }
}
